// Phase NOTARY-FOCUS N2 — Multilingual Notary landing copy
// Powers /zh /ja /ko /ru /ar /de /fr — and (N-G) /es /pt /vi /id notary landings

export interface NotaryLocale {
  locale: "zh" | "ja" | "ko" | "ru" | "ar" | "de" | "fr" | "es" | "pt" | "vi" | "id";
  hreflang: string;       // zh-CN, ja-JP, ...
  path: string;           // /zh/notary
  dir?: "ltr" | "rtl";
  langName: string;       // displayed in the language switcher
  title: string;          // <title> (≤60 chars ideally)
  description: string;    // meta description
  h1: string;
  hero: string;
  ctaConsult: string;
  ctaCall: string;
  factsLabel: { price: string; turnaround: string; signer: string; usedIn: string; office: string };
  facts: { price: string; turnaround: string; signer: string; usedIn: string; office: string };
  servicesH2: string;
  categories: Record<
    "signature" | "affidavit" | "copy" | "corporate" | "property" | "intl",
    string
  >;
  stepsH2: string;
  steps: { title: string; body: string }[];
  faqH2: string;
  faqs: { q: string; a: string }[];
  bottomNudge: string;
  contactLine: string; // "LINE", localized
  contactPhone: string; // localized phone label
}

const COMMON_FACTS_LABELS = {
  zh: { price: "起价", turnaround: "完成时间", signer: "签字人", usedIn: "使用国家", office: "办公室" },
  ja: { price: "料金", turnaround: "所要時間", signer: "署名者", usedIn: "使用国", office: "事務所" },
  ko: { price: "시작 가격", turnaround: "소요 시간", signer: "서명자", usedIn: "사용 국가", office: "사무실" },
  ru: { price: "от", turnaround: "Срок", signer: "Подписант", usedIn: "Принимается в", office: "Офис" },
  ar: { price: "السعر يبدأ من", turnaround: "المدة", signer: "الموقّع", usedIn: "مقبول في", office: "المكتب" },
  de: { price: "Ab", turnaround: "Bearbeitungszeit", signer: "Unterzeichner", usedIn: "Akzeptiert in", office: "Büro" },
  fr: { price: "À partir de", turnaround: "Délai", signer: "Signataire", usedIn: "Accepté dans", office: "Bureau" },
  es: { price: "Desde", turnaround: "Plazo", signer: "Firmante", usedIn: "Aceptado en", office: "Oficina" },
  pt: { price: "A partir de", turnaround: "Prazo", signer: "Signatário", usedIn: "Aceito em", office: "Escritório" },
  vi: { price: "Từ", turnaround: "Thời gian", signer: "Người ký", usedIn: "Sử dụng tại", office: "Văn phòng" },
  id: { price: "Mulai dari", turnaround: "Waktu proses", signer: "Penandatangan", usedIn: "Berlaku di", office: "Kantor" },
} as const;

const COMMON_FACTS_VALUES = {
  price: "฿500",
  turnaround: { zh: "当天/1个工作日", ja: "当日 / 1 営業日", ko: "당일 / 1 영업일", ru: "В тот же день / 1 раб. день", ar: "نفس اليوم / يوم عمل", de: "Selber Tag / 1 Werktag", fr: "Jour même / 1 jour ouvré", es: "Mismo día / 1 día hábil", pt: "No mesmo dia / 1 dia útil", vi: "Trong ngày / 1 ngày làm việc", id: "Hari yang sama / 1 hari kerja" },
  signer: { zh: "泰国律师公会注册公证律师", ja: "タイ弁護士会登録の公証弁護士", ko: "태국변호사협회 등록 공증변호사", ru: "Нотариус-адвокат (Совет адвокатов Таиланда)", ar: "محامي توثيق مسجل لدى مجلس المحامين التايلاندي", de: "Notarjurist, eingetragen bei der thailändischen Anwaltskammer", fr: "Avocat-notaire inscrit au Conseil des avocats thaïlandais", es: "Abogado-notario inscrito en el Colegio de Abogados de Tailandia", pt: "Advogado-notário registado na Ordem dos Advogados da Tailândia", vi: "Luật sư công chứng đăng ký với Đoàn Luật sư Thái Lan", id: "Notaris-pengacara terdaftar di Dewan Pengacara Thailand" },
  usedIn: { zh: "120 多个国家", ja: "120 か国以上", ko: "120여 개국", ru: "120+ стран", ar: "أكثر من 120 دولة", de: "120+ Länder", fr: "120+ pays", es: "120+ países", pt: "120+ países", vi: "Hơn 120 quốc gia", id: "120+ negara" },
  office: { zh: "曼谷 · DBD 0435567000061", ja: "バンコク · DBD 0435567000061", ko: "방콕 · DBD 0435567000061", ru: "Бангкок · DBD 0435567000061", ar: "بانكوك · DBD 0435567000061", de: "Bangkok · DBD 0435567000061", fr: "Bangkok · DBD 0435567000061", es: "Bangkok · DBD 0435567000061", pt: "Bangkok · DBD 0435567000061", vi: "Bangkok · DBD 0435567000061", id: "Bangkok · DBD 0435567000061" },
} as const;

function facts(l: keyof typeof COMMON_FACTS_LABELS) {
  return {
    price: COMMON_FACTS_VALUES.price,
    turnaround: COMMON_FACTS_VALUES.turnaround[l],
    signer: COMMON_FACTS_VALUES.signer[l],
    usedIn: COMMON_FACTS_VALUES.usedIn[l],
    office: COMMON_FACTS_VALUES.office[l],
  };
}

export const NOTARY_LOCALES: NotaryLocale[] = [
  {
    locale: "zh",
    hreflang: "zh-CN",
    path: "/zh/notary",
    langName: "中文",
    title: "曼谷公证服务 — 泰国律师公会注册公证律师 | NYC",
    description: "曼谷专业公证服务：签字认证、文件副本、宣誓书、授权书、公司决议。当天完成,500 泰铢起,适用于 120 多个国家。",
    h1: "曼谷公证服务",
    hero: "由泰国律师公会注册公证律师签发的公证服务,涵盖 30 多种公证行为 — 适用于中国领事认证、Apostille、海外婚姻、房产授权书。",
    ctaConsult: "微信 / LINE 咨询",
    ctaCall: "电话 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.zh,
    facts: facts("zh"),
    servicesH2: "9 大公证服务类别",
    categories: {
      signature: "签字认证(Acknowledgement / Witness)",
      affidavit: "宣誓书 / Jurat / Oath",
      copy: "文件副本认证(Certified True Copy)",
      corporate: "公司决议 / 授权书(POA)",
      property: "房产 / 遗产文件",
      intl: "签证 / 海外使用文件",
    },
    stepsH2: "办理流程(4 步)",
    steps: [
      { title: "1. 微信 / LINE 发送文件", body: "通过 LINE @nycli 或电邮发送文件扫描件和护照页。我们 15 分钟内确认服务类型和费用。" },
      { title: "2. 预约或步入办公室", body: "曼谷办公室位于 Asoke 附近,周一至周六 09:00-18:00。" },
      { title: "3. 当面签字", body: "在公证律师当面签字。整个过程约 20 分钟。" },
      { title: "4. 领取盖章文件", body: "当天领取公证文件,可继续办理 MFA 认证 + 中国领事认证。" },
    ],
    faqH2: "常见问题",
    faqs: [
      { q: "中国大陆是否接受 Apostille?", a: "中国自 2023 年 11 月 7 日起适用《海牙取消认证公约》,但泰国的加入要到 2027 年 2 月 28 日才生效。因此在此之前,泰国出具的文件仍须走完整领事认证链:公证律师 → 泰国外交部领事司认证 → 中国驻泰国使领馆认证,之后方可在中国大陆使用。" },
      { q: "我需要将文件翻译成中文吗?", a: "绝大多数情况需要。中国内地机关只接受简体中文译文,并要求译文与原件一并提交、由具资质的翻译机构盖章;香港、澳门及台湾地区通常使用繁体中文。译文中的姓名拼写必须与护照完全一致,否则容易被退件。" },
      { q: "可以在公证律师面前用中文沟通吗?", a: "可以。我们安排中文人员在场,逐条说明宣誓书、授权书或公司决议的法律含义,并核对姓名、身份证号与日期,确保您在完全理解文件内容的前提下签字。签字必须由本人当面完成,不能代签。" },
      { q: "公证文件多久内有效?", a: "公证行为本身没有法定有效期,期限由使用方决定。实务中,单身证明、无犯罪记录、授权书等文件,中国机关通常要求出具日期在 3 至 6 个月以内;房产与继承类文件则常按具体案件另行要求,建议先向受理机关确认。" },
      { q: "可以加急当日完成吗?", a: "公证环节多数可当天完成,前提是本人携带有效护照到场且文件内容无需改写。若还需泰国外交部认证与中国使领馆认证,整体一般需要 7-10 个工作日,具体取决于两个机关当期的排队情况。" },

    ],
    bottomNudge: "需要翻译 + 公证 + 领事认证打包服务?请发送 LINE 联系我们。",
    contactLine: "LINE @nycli",
    contactPhone: "电话 083-249-4999",
  },
  {
    locale: "ja",
    hreflang: "ja-JP",
    path: "/ja/notary",
    langName: "日本語",
    title: "バンコク 公証サービス — タイ弁護士会登録公証弁護士 | NYC",
    description: "バンコクの公証サービス:署名認証、書類謄本、宣誓書、委任状、会社決議。500バーツから即日対応、120か国以上で使用可能。",
    h1: "バンコク 公証サービス",
    hero: "タイ弁護士会登録の公証弁護士による30種類以上の公証行為に対応 — 婚姻届、Spouse Visa、不動産委任状、会社設立に使用可能。",
    ctaConsult: "LINE で相談",
    ctaCall: "電話 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.ja,
    facts: facts("ja"),
    servicesH2: "9つの公証カテゴリー",
    categories: {
      signature: "署名認証(Acknowledgement / Witness)",
      affidavit: "宣誓書 / Jurat / Oath",
      copy: "書類謄本認証(Certified True Copy)",
      corporate: "会社決議 / 委任状(POA)",
      property: "不動産 / 相続書類",
      intl: "ビザ / 海外使用書類",
    },
    stepsH2: "ご利用の流れ(4ステップ)",
    steps: [
      { title: "1. LINE で書類を送信", body: "LINE @nycli にパスポートと書類のスキャンを送信。15分以内に種類と料金を確認します。" },
      { title: "2. 来店予約", body: "アソーク近くのバンコクオフィス、月〜土 09:00-18:00。" },
      { title: "3. 公証弁護士の前で署名", body: "公証弁護士の前で署名(約20分)。" },
      { title: "4. 公証済み書類を受領", body: "当日中に受領可能。MFA + 日本大使館認証も対応します。" },
    ],
    faqH2: "よくある質問",
    faqs: [
      { q: "日本ではApostilleは使えますか?", a: "日本はハーグ条約の締約国ですが、タイの加入発効は2027年2月28日です。それまでタイで作成した書類は、公証弁護士 → タイ外務省領事局の認証 → 在タイ日本国大使館の領事認証という流れが必要で、アポスティーユでの代替はできません。" },
      { q: "婚姻届に必要な書類は?", a: "一般的には独身証明書(公証済み)、出生証明書、パスポート写し、タイ側の住居登録(タビアンバーン)と身分証、そして各書類の日本語訳が求められます。市区町村役場ごとに追加書類や有効期限の指定があるため、提出先へ事前確認してください。" },
      { q: "日本語で対応してもらえますか?", a: "はい。日本語対応スタッフが宣誓書や委任状の条項を一つずつ説明し、氏名のローマ字表記・生年月日・住所がパスポートや戸籍と一致しているかを確認します。署名はご本人が公証弁護士の面前で行う必要があります。" },
      { q: "公証費用はいくらですか?", a: "公証は1件500バーツから、書類の種類・枚数・署名者の人数によって変わります。外務省認証や大使館認証、翻訳を含む場合は各機関の実費が別途必要です。書類のスキャンをお送りいただければ事前にお見積りします。" },
      { q: "Spouse Visa用のAffidavit of Supportは対応していますか?", a: "はい。扶養能力を示す宣誓供述書(Jurat形式)を英文で作成し、公証弁護士の面前で宣誓・署名いただきます。収入証明や預金残高証明を添付するのが一般的で、提出先の指定様式があればその書式に合わせて作成します。" },

    ],
    bottomNudge: "翻訳 + 公証 + 大使館認証のパッケージをご希望の方はLINEまでご相談ください。",
    contactLine: "LINE @nycli",
    contactPhone: "電話 083-249-4999",
  },
  {
    locale: "ko",
    hreflang: "ko-KR",
    path: "/ko/notary",
    langName: "한국어",
    title: "방콕 공증 서비스 — 태국변호사협회 등록 공증변호사 | NYC",
    description: "방콕 공증 서비스: 서명 인증, 사본 인증, 진술서, 위임장, 법인 결의. 500바트부터, 당일 처리, 120여 개국에서 사용 가능.",
    h1: "방콕 공증 서비스",
    hero: "태국변호사협회에 등록된 공증변호사가 30여 종의 공증 업무를 처리 — F-6 비자, Apostille, 한국 부동산 위임장, 회사 결의에 사용 가능.",
    ctaConsult: "LINE 상담",
    ctaCall: "전화 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.ko,
    facts: facts("ko"),
    servicesH2: "9가지 공증 카테고리",
    categories: {
      signature: "서명 인증 (Acknowledgement / Witness)",
      affidavit: "진술서 / Jurat / Oath",
      copy: "사본 인증 (Certified True Copy)",
      corporate: "법인 결의 / 위임장 (POA)",
      property: "부동산 / 상속 서류",
      intl: "비자 / 해외 사용 서류",
    },
    stepsH2: "처리 절차 (4단계)",
    steps: [
      { title: "1. LINE 으로 서류 전송", body: "LINE @nycli 로 여권과 서류 스캔을 보내주세요. 15분 이내에 종류와 비용을 확인해 드립니다." },
      { title: "2. 사무실 방문 예약", body: "Asoke 인근 방콕 사무실, 월–토 09:00-18:00." },
      { title: "3. 공증변호사 앞에서 서명", body: "공증변호사 앞에서 서명 (약 20분 소요)." },
      { title: "4. 공증 서류 수령", body: "당일 수령 가능. MFA Apostille + 한국에서 사용 준비 완료." },
    ],
    faqH2: "자주 묻는 질문",
    faqs: [
      { q: "한국은 Apostille 을 인정합니까?", a: "한국은 2007년부터 아포스티유 협약 당사국이지만, 태국의 협약 발효일은 2027년 2월 28일입니다. 그때까지 태국에서 작성한 서류는 공증변호사 → 태국 외교부 영사국 인증 → 주태국 대한민국 대사관 영사확인 순서를 거쳐야 한국에서 사용할 수 있습니다." },
      { q: "F-6 비자에 필요한 서류는?", a: "일반적으로 혼인관계증명(독신증명), 출생증명서, 범죄경력증명서, 여권 사본, 태국 주소지 증명(타비안반)과 각 서류의 한국어 번역본이 필요합니다. 관할 출입국·외국인청과 재외공관마다 추가 서류와 유효기간 기준이 다르므로 접수 전 확인하시기 바랍니다." },
      { q: "한국어로 상담 가능합니까?", a: "가능합니다. 한국어 담당자가 진술서·위임장의 문구를 항목별로 설명하고, 여권상 영문 성명과 생년월일, 주소 표기가 일치하는지 확인합니다. 서명은 반드시 본인이 공증변호사 앞에서 직접 해야 하며 대리 서명은 인정되지 않습니다." },
      { q: "공증 비용은 얼마입니까?", a: "공증은 건당 500바트부터이며 서류 종류, 장수, 서명자 수에 따라 달라집니다. 외교부 인증, 대사관 영사확인, 번역이 포함되면 각 기관의 실비가 별도로 발생합니다. 서류 스캔본을 보내주시면 사전에 총액을 안내해 드립니다." },
      { q: "당일 처리 가능합니까?", a: "본인이 유효한 여권을 지참하고 방문하며 문안 수정이 필요 없는 경우 공증 단계는 당일 완료됩니다. 다만 외교부 인증과 대사관 영사확인까지 포함하면 통상 7~10 영업일이 소요되며, 각 기관의 접수 대기 상황에 따라 달라집니다." },

    ],
    bottomNudge: "번역 + 공증 + Apostille 패키지를 원하시면 LINE 으로 연락 주세요.",
    contactLine: "LINE @nycli",
    contactPhone: "전화 083-249-4999",
  },
  {
    locale: "ru",
    hreflang: "ru-RU",
    path: "/ru/notary",
    langName: "Русский",
    title: "Нотариальные услуги в Бангкоке — Нотариус-адвокат | NYC",
    description: "Нотариальные услуги в Бангкоке: заверение подписи, копий, аффидевитов, доверенностей, корпоративных решений. От 500 бат, в день обращения.",
    h1: "Нотариальные услуги в Бангкоке",
    hero: "Нотариус-адвокат, зарегистрированный в Совете адвокатов Таиланда, выполняет более 30 видов нотариальных действий — доверенности на недвижимость в России, аффидевиты для брака, корпоративные документы.",
    ctaConsult: "Связаться через LINE",
    ctaCall: "Телефон 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.ru,
    facts: facts("ru"),
    servicesH2: "9 категорий нотариальных услуг",
    categories: {
      signature: "Заверение подписи (Acknowledgement / Witness)",
      affidavit: "Аффидевит / Jurat / Oath",
      copy: "Заверение копий (Certified True Copy)",
      corporate: "Корпоративные решения / Доверенность",
      property: "Недвижимость / Наследство",
      intl: "Виза / Использование за рубежом",
    },
    stepsH2: "Процесс (4 шага)",
    steps: [
      { title: "1. Отправьте документы в LINE", body: "Через LINE @nycli отправьте скан паспорта и документа. В течение 15 минут подтвердим тип услуги и стоимость." },
      { title: "2. Назначение визита", body: "Офис в Бангкоке (район Asoke), пн–сб 09:00-18:00." },
      { title: "3. Подпись в присутствии нотариуса", body: "Подпись в присутствии нотариуса-адвоката (~20 минут)." },
      { title: "4. Получение заверенного документа", body: "Получение в тот же день. Готов для MFA Apostille и использования в России." },
    ],
    faqH2: "Часто задаваемые вопросы",
    faqs: [
      { q: "Россия принимает Apostille?", a: "Россия является участником Гаагской конвенции, однако для Таиланда конвенция вступает в силу только 28 февраля 2027 года. До этой даты тайские документы легализуются по консульской цепочке: нотариус-адвокат, затем Департамент консульских дел МИД Таиланда и далее консульский отдел Посольства России в Бангкоке." },
      { q: "Можно ли оформить доверенность для России в Таиланде?", a: "Да. Доверенность составляется с формулировками, которые требует принимающий орган, подписывается лично в присутствии нотариуса-адвоката, переводится на русский язык и проходит легализацию МИД Таиланда и российского консульства. Написание фамилии должно совпадать с загранпаспортом, иначе Росреестр или банк вернут документ." },
      { q: "Возможна ли консультация на русском языке?", a: "Да. Русскоязычный сотрудник построчно объясняет текст доверенности или аффидевита, сверяет паспортные данные, даты и адреса перед подписанием. Подпись ставится только лично: нотариус-адвокат удостоверяет присутствие подписанта, а не содержание сделки." },
      { q: "Сколько стоит услуга?", a: "Нотариальное действие стоит от 500 бат и зависит от вида документа, количества страниц и числа подписантов. Перевод, легализация в МИД Таиланда и консульский сбор оплачиваются отдельно по тарифам этих органов. Пришлите скан документа, и мы рассчитаем итоговую сумму заранее." },
      { q: "Можно ли получить документ в день обращения?", a: "Да, нотариальный этап обычно занимает около двадцати минут и завершается в тот же день, если вы приходите с действующим паспортом и текст не требует правок. Полная консульская цепочка с МИД и посольством занимает, как правило, от семи до четырнадцати рабочих дней." },

    ],
    bottomNudge: "Нужен пакет «перевод + нотариус + апостиль»? Свяжитесь с нами через LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "Телефон 083-249-4999",
  },
  {
    locale: "ar",
    hreflang: "ar",
    path: "/ar/notary",
    dir: "rtl",
    langName: "العربية",
    title: "خدمات التوثيق في بانكوك — محامي توثيق معتمد | NYC",
    description: "خدمات التوثيق القانوني في بانكوك: توثيق التوقيع، نسخ مصدقة، إقرارات قانونية، وكالات، قرارات شركات. تبدأ من 500 بات، إنجاز في نفس اليوم.",
    h1: "خدمات التوثيق في بانكوك",
    hero: "محامي توثيق مسجل لدى مجلس المحامين التايلاندي يقدم أكثر من 30 نوعًا من خدمات التوثيق — وكالات، إقرارات الزواج، تصديق وثائق الشركات للاستخدام في الدول العربية.",
    ctaConsult: "تواصل عبر LINE",
    ctaCall: "اتصل 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.ar,
    facts: facts("ar"),
    servicesH2: "9 فئات من خدمات التوثيق",
    categories: {
      signature: "توثيق التوقيع (Acknowledgement / Witness)",
      affidavit: "إقرار قانوني / Jurat / Oath",
      copy: "نسخة مصدقة (Certified True Copy)",
      corporate: "قرارات الشركة / وكالة (POA)",
      property: "العقارات / الميراث",
      intl: "تأشيرة / استخدام خارج تايلاند",
    },
    stepsH2: "خطوات الخدمة (4 خطوات)",
    steps: [
      { title: "1. أرسل المستندات عبر LINE", body: "أرسل صورة من جواز السفر والمستند عبر LINE @nycli. سنؤكد نوع الخدمة والتكلفة خلال 15 دقيقة." },
      { title: "2. تحديد موعد", body: "المكتب في بانكوك بالقرب من Asoke، الإثنين–السبت 09:00-18:00." },
      { title: "3. التوقيع أمام المحامي", body: "التوقيع أمام محامي التوثيق (حوالي 20 دقيقة)." },
      { title: "4. استلام المستند الموثق", body: "استلام في نفس اليوم. جاهز للتصديق من وزارة الخارجية والسفارة." },
    ],
    faqH2: "أسئلة شائعة",
    faqs: [
      { q: "هل تُقبل خدمة Apostille في دول الخليج؟", a: "الإمارات والسعودية والبحرين وعُمان أعضاء في اتفاقية لاهاي، لكن انضمام تايلاند لا يسري إلا في 28 فبراير 2027. لذلك تُصدَّق المستندات التايلاندية حتى ذلك التاريخ عبر السلسلة القنصلية: محامي التوثيق، ثم إدارة الشؤون القنصلية بوزارة الخارجية التايلاندية، ثم سفارة بلد الاستخدام في بانكوك." },
      { q: "هل يمكنني إعداد وكالة لاستخدامها في بلدي العربي؟", a: "نعم. تُصاغ الوكالة بالصيغة التي تطلبها الجهة المستقبِلة، وتُوقَّع شخصيًا أمام محامي التوثيق، ثم تُترجم إلى العربية وتُصدَّق من وزارة الخارجية التايلاندية وسفارة بلدك. يجب أن يطابق اسمك في الوكالة جواز السفر حرفيًا لتفادي الرفض." },
      { q: "هل تقدمون خدمة باللغة العربية؟", a: "نعم. يشرح لك موظف يتحدث العربية بنود الإقرار أو الوكالة بندًا بندًا، ويتحقق من الأسماء وأرقام الهوية والتواريخ قبل التوقيع. التوقيع يتم حضوريًا فقط، إذ يشهد المحامي على هوية الموقّع لا على صحة مضمون الاتفاق." },
      { q: "كم تستغرق الخدمة؟", a: "مرحلة التوثيق تُنجز عادة في اليوم نفسه وتستغرق نحو عشرين دقيقة، بشرط حضورك بجواز سفر ساري وعدم الحاجة إلى تعديل الصياغة. أما التصديق الكامل من وزارة الخارجية والسفارة فيستغرق عادة من سبعة إلى أربعة عشر يوم عمل حسب ازدحام الجهتين." },
      { q: "ما هي التكلفة؟", a: "تبدأ رسوم التوثيق من 500 بات وتختلف بحسب نوع المستند وعدد الصفحات وعدد الموقّعين. تُضاف إليها رسوم الترجمة وتصديق وزارة الخارجية والسفارة وفق تعرفة كل جهة. أرسل نسخة ممسوحة ضوئيًا لنقدّم لك تقديرًا شاملًا مسبقًا." },

    ],
    bottomNudge: "تحتاج باقة ترجمة + توثيق + تصديق سفارة؟ تواصل معنا عبر LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "اتصل 083-249-4999",
  },
  {
    locale: "de",
    hreflang: "de-DE",
    path: "/de/notary",
    langName: "Deutsch",
    title: "Notarservice Bangkok — Notarjurist (thailändische Anwaltskammer) | NYC",
    description: "Notarielle Dienstleistungen in Bangkok: Unterschriftsbeglaubigung, beglaubigte Kopien, eidesstattliche Erklärungen, Vollmachten. Ab 500 Baht, Bearbeitung am selben Tag.",
    h1: "Notarservice Bangkok",
    hero: "Notarjurist, eingetragen bei der thailändischen Anwaltskammer, mit über 30 notariellen Dienstleistungen — Ehefähigkeitszeugnis-Affidavit, Vollmachten für Deutschland, Unternehmensbeschlüsse mit Apostille.",
    ctaConsult: "LINE Beratung",
    ctaCall: "Telefon 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.de,
    facts: facts("de"),
    servicesH2: "9 Kategorien notarieller Leistungen",
    categories: {
      signature: "Unterschriftsbeglaubigung (Acknowledgement / Witness)",
      affidavit: "Eidesstattliche Erklärung / Jurat / Oath",
      copy: "Beglaubigte Kopie (Certified True Copy)",
      corporate: "Unternehmensbeschluss / Vollmacht (POA)",
      property: "Immobilien / Nachlass",
      intl: "Visum / Auslandsverwendung",
    },
    stepsH2: "Ablauf (4 Schritte)",
    steps: [
      { title: "1. Dokumente per LINE senden", body: "Senden Sie Reisepass und Dokumentenscans an LINE @nycli. Wir bestätigen Art und Kosten innerhalb von 15 Minuten." },
      { title: "2. Termin vereinbaren", body: "Büro in Bangkok (Nähe Asoke), Mo–Sa 09:00–18:00." },
      { title: "3. Unterschrift vor dem Notarjurist", body: "Persönliche Unterschrift vor dem Notarjurist (~20 Minuten)." },
      { title: "4. Beglaubigtes Dokument abholen", body: "Abholung am selben Tag. Bereit für MFA-Apostille und Verwendung in Deutschland." },
    ],
    faqH2: "Häufige Fragen",
    faqs: [
      { q: "Akzeptiert Deutschland thailändische Apostillen?", a: "Deutschland ist Vertragsstaat des Haager Übereinkommens, für Thailand tritt es jedoch erst am 28. Februar 2027 in Kraft. Bis dahin läuft die Beglaubigung thailändischer Urkunden über die konsularische Kette: Notarjurist, danach das Konsularamt des thailändischen Außenministeriums und anschließend die Deutsche Botschaft in Bangkok." },
      { q: "Wird das deutsche Standesamt das Affidavit anerkennen?", a: "Standesämter verlangen in der Regel die legalisierte Originalurkunde samt Übersetzung durch einen in Deutschland öffentlich bestellten und beeidigten Übersetzer. Viele Ämter setzen zusätzlich eine Ausstellung innerhalb der letzten sechs Monate voraus. Klären Sie die genauen Anforderungen vorab mit dem zuständigen Standesamt." },
      { q: "Können wir die Beratung auf Deutsch durchführen?", a: "Ja, wir erläutern Vollmachten und eidesstattliche Erklärungen auf Deutsch Satz für Satz und prüfen vor der Unterschrift Namensschreibweise, Geburtsdatum und Anschrift gegen Ihren Reisepass. Unterschrieben wird ausschließlich persönlich vor dem Notarjurist; eine Vertretung ist nicht zulässig." },
      { q: "Wie hoch sind die Kosten?", a: "Die notarielle Handlung beginnt bei 500 Baht und richtet sich nach Urkundenart, Seitenzahl und Anzahl der Unterzeichner. Übersetzung, Legalisation beim Außenministerium und Botschaftsgebühren werden nach den Tarifen der jeweiligen Stelle gesondert berechnet. Senden Sie uns einen Scan für eine Gesamtkalkulation." },
      { q: "Ist eine Bearbeitung am selben Tag möglich?", a: "Der notarielle Teil dauert etwa zwanzig Minuten und ist am selben Tag abgeschlossen, sofern Sie mit gültigem Reisepass erscheinen und der Text nicht geändert werden muss. Die vollständige Kette über Außenministerium und Botschaft benötigt üblicherweise sieben bis vierzehn Werktage." },

    ],
    bottomNudge: "Sie benötigen ein Komplettpaket aus Übersetzung + Notar + Apostille? Schreiben Sie uns auf LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "Telefon 083-249-4999",
  },
  {
    locale: "fr",
    hreflang: "fr-FR",
    path: "/fr/notary",
    langName: "Français",
    title: "Notaire Bangkok — Avocat-notaire (Conseil des avocats thaïlandais) | NYC",
    description: "Services notariaux à Bangkok : authentification de signature, copies certifiées, déclarations sous serment, procurations. À partir de 500 bahts, jour même.",
    h1: "Services notariaux à Bangkok",
    hero: "Avocat-notaire inscrit au Conseil des avocats thaïlandais, plus de 30 actes notariés disponibles — procurations pour la France, déclarations de célibat, résolutions d'entreprise avec apostille.",
    ctaConsult: "Contacter via LINE",
    ctaCall: "Téléphone 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.fr,
    facts: facts("fr"),
    servicesH2: "9 catégories d'actes notariés",
    categories: {
      signature: "Authentification de signature (Acknowledgement / Witness)",
      affidavit: "Déclaration sous serment / Jurat / Oath",
      copy: "Copie certifiée conforme (Certified True Copy)",
      corporate: "Résolutions d'entreprise / Procuration (POA)",
      property: "Immobilier / Succession",
      intl: "Visa / Usage à l'étranger",
    },
    stepsH2: "Étapes (4 étapes)",
    steps: [
      { title: "1. Envoyer les documents via LINE", body: "Envoyez vos documents et passeport via LINE @nycli. Nous confirmons le service et le tarif sous 15 minutes." },
      { title: "2. Prendre rendez-vous", body: "Bureau à Bangkok (proche Asoke), Lun–Sam 09:00–18:00." },
      { title: "3. Signature devant l'avocat-notaire", body: "Signature en personne devant l'avocat-notaire (environ 20 minutes)." },
      { title: "4. Réception du document notarié", body: "Retrait le jour même. Prêt pour apostille MFA et utilisation en France." },
    ],
    faqH2: "Questions fréquentes",
    faqs: [
      { q: "La France accepte-t-elle l'apostille thaïlandaise ?", a: "La France est partie à la Convention de La Haye, mais l'adhésion de la Thaïlande n'entrera en vigueur que le 28 février 2027. Jusqu'à cette date, les actes thaïlandais suivent la voie consulaire : avocat-notaire, puis Département des affaires consulaires du ministère thaïlandais des Affaires étrangères, puis Ambassade de France à Bangkok." },
      { q: "Faut-il une traduction assermentée française ?", a: "Oui, les mairies, tribunaux et notaires français exigent une traduction réalisée par un traducteur inscrit sur la liste d'une cour d'appel, jointe à l'original légalisé. L'orthographe des noms doit correspondre exactement au passeport et à l'acte d'état civil, faute de quoi le dossier est renvoyé." },
      { q: "Pouvez-vous conseiller en français ?", a: "Oui, un intervenant francophone vous explique chaque clause de la procuration ou de l'attestation sur l'honneur et vérifie noms, dates et adresses avant la signature. La signature se fait uniquement en personne : l'avocat-notaire atteste de votre identité, non du contenu de l'accord." },
      { q: "Quel est le coût ?", a: "L'acte notarié débute à 500 bahts et varie selon le type de document, le nombre de pages et le nombre de signataires. La traduction, la légalisation au ministère des Affaires étrangères et les frais consulaires s'ajoutent au tarif propre à chaque administration. Envoyez un scan pour une estimation complète." },
      { q: "Possible le jour même ?", a: "Oui, l'étape notariale dure environ vingt minutes et s'achève le jour même si vous venez avec un passeport valide et que le texte n'a pas à être modifié. La chaîne complète incluant le ministère et l'ambassade demande en général sept à quatorze jours ouvrés." },

    ],
    bottomNudge: "Besoin d'un pack traduction + notaire + apostille ? Contactez-nous via LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "Téléphone 083-249-4999",
  },
  // ─────────── N-G additions: Spanish / Portuguese / Vietnamese / Indonesian ───────────
  {
    locale: "es",
    hreflang: "es-ES",
    path: "/es/notary",
    langName: "Español",
    title: "Notario en Bangkok — Abogado-notario colegiado | NYC Legal",
    description: "Servicios notariales en Bangkok: certificación de firmas, copias fieles, declaraciones juradas, poderes, actas societarias. Desde 500 THB, mismo día, aceptado en 120+ países.",
    h1: "Servicios de Notario Público en Bangkok",
    hero: "Actos notariales realizados por un abogado-notario inscrito en el Colegio de Abogados de Tailandia — más de 30 servicios para apostilla, legalización consular, matrimonio en el extranjero y poderes inmobiliarios.",
    ctaConsult: "Consulta por LINE / WhatsApp",
    ctaCall: "Llamar al 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.es,
    facts: facts("es"),
    servicesH2: "Nuestras 6 categorías de servicios notariales",
    categories: {
      signature: "Certificación de firmas (Acknowledgement / Witness)",
      affidavit: "Declaraciones juradas / Jurat / Oath",
      copy: "Copias fieles certificadas (Certified True Copy)",
      corporate: "Actas societarias / Poderes (POA)",
      property: "Documentos inmobiliarios / sucesorios",
      intl: "Visado / documentos para uso en el extranjero",
    },
    stepsH2: "Cómo trabajamos (4 pasos)",
    steps: [
      { title: "1. Envíe el documento por LINE / email", body: "Escanee su documento y pasaporte y envíelos por LINE @nycli o email. Confirmamos el servicio y el precio en 15 minutos." },
      { title: "2. Reserve o visite la oficina", body: "Oficina en Bangkok (cerca de Asoke MRT). Lunes a sábado 09:00–18:00." },
      { title: "3. Firme ante el notario", body: "El abogado-notario verifica su identidad y presencia la firma. El proceso dura unos 20 minutos." },
      { title: "4. Recoja el documento sellado", body: "Retire el documento notarizado el mismo día. Podemos continuar con la legalización del MFA y la del consulado." },
    ],
    faqH2: "Preguntas frecuentes",
    faqs: [
      { q: "¿España acepta la apostilla de Tailandia?", a: "España es parte del Convenio de La Haya, pero la adhesión de Tailandia no entra en vigor hasta el 28 de febrero de 2027. Hasta esa fecha los documentos tailandeses siguen la vía consular: abogado-notario, después el Departamento Consular del Ministerio de Asuntos Exteriores tailandés y por último la Embajada de España en Bangkok." },
      { q: "¿Necesito una traducción jurada al español?", a: "Una traducción jurada al español es requerida por la mayoría de los organismos españoles, los cuales exigen que la traducción sea realizada por un traductor jurado habilitado por el Ministerio de Asuntos Exteriores, Unión Europea y Cooperación (MAEC)." },
      { q: "¿Pueden atenderme en español?", a: "La asistencia en español está disponible para atender sus trámites. Ofrecemos asistencia en español para facilitar la comunicación y el procesamiento de todos sus documentos oficiales y legales." },
      { q: "¿Cuánto cuesta?", a: "La gestión de los documentos varía según el tipo de documento presentado. Cada trámite se ajusta a las características y especificaciones de la documentación correspondiente." },
      { q: "¿Es posible el mismo día?", a: "La tramitación el mismo día es factible para la mayoría de las solicitudes. La mayoría de los actos y gestiones documentales se completan el mismo día." },
    ],
    bottomNudge: "¿Necesita un paquete traducción + notario + apostilla? Contáctenos por LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "Teléfono 083-249-4999",
  },
  {
    locale: "pt",
    hreflang: "pt-PT",
    path: "/pt/notary",
    langName: "Português",
    title: "Notário em Banguecoque — Advogado-notário registado | NYC Legal",
    description: "Serviços notariais em Banguecoque: reconhecimento de assinaturas, cópias autenticadas, declarações, procurações, atas societárias. A partir de 500 THB, no mesmo dia, aceito em 120+ países.",
    h1: "Serviços de Notário Público em Banguecoque",
    hero: "Atos notariais por advogado-notário registado na Ordem dos Advogados da Tailândia — 30+ serviços para apostila, legalização consular, casamento no estrangeiro e procurações imobiliárias.",
    ctaConsult: "Consulta por LINE / WhatsApp",
    ctaCall: "Ligar 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.pt,
    facts: facts("pt"),
    servicesH2: "6 categorias de serviços notariais",
    categories: {
      signature: "Reconhecimento de assinaturas (Acknowledgement / Witness)",
      affidavit: "Declarações / Jurat / Oath",
      copy: "Cópias autenticadas (Certified True Copy)",
      corporate: "Atas societárias / Procurações (POA)",
      property: "Documentos imobiliários / sucessórios",
      intl: "Visto / documentos para uso no estrangeiro",
    },
    stepsH2: "Como trabalhamos (4 passos)",
    steps: [
      { title: "1. Envie o documento por LINE / email", body: "Digitalize o seu documento e passaporte e envie por LINE @nycli ou email. Confirmamos o serviço e o preço em 15 minutos." },
      { title: "2. Marque ou visite o escritório", body: "Escritório em Banguecoque (junto ao MRT Asoke). Segunda a sábado, 09:00–18:00." },
      { title: "3. Assine perante o notário", body: "O advogado-notário verifica a sua identidade e presencia a assinatura. Demora cerca de 20 minutos." },
      { title: "4. Levante o documento selado", body: "Retire o documento notarizado no mesmo dia. Podemos avançar com legalização MFA e consular." },
    ],
    faqH2: "Perguntas frequentes",
    faqs: [
      { q: "Portugal e o Brasil aceitam apostila da Tailândia?", a: "Portugal e o Brasil são partes da Convenção da Haia, mas a adesão da Tailândia só entra em vigor em 28 de fevereiro de 2027. Até lá, os documentos tailandeses seguem a via consular: advogado-notário, Departamento Consular do Ministério dos Negócios Estrangeiros tailandês e, por fim, a embaixada de Portugal ou do Brasil em Banguecoque." },
      { q: "Preciso de tradução certificada para português?", a: "A tradução certificada para português é exigida por tribunais e conservatórias portuguesas, bem como por cartórios brasileiros. Estes organismos requerem que o documento seja traduzido por um tradutor certificado." },
      { q: "Podem atender-me em português?", a: "O atendimento em português está disponível para prestar todo o suporte necessário. Oferecemos apoio em português, incluindo as variantes de Portugal (PT-PT) e do Brasil (PT-BR)." },
      { q: "Qual é o custo?", a: "O processamento dos serviços varia consoante o tipo de documento apresentado. A análise e o tratamento dependem da categoria de cada documento fornecido." },
      { q: "É possível no mesmo dia?", a: "A conclusão dos serviços no mesmo dia é possível para a maior parte dos pedidos. A maioria dos atos é concluída no próprio dia." },
    ],
    bottomNudge: "Precisa de um pacote tradução + notário + apostila? Contacte-nos via LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "Telefone 083-249-4999",
  },
  {
    locale: "vi",
    hreflang: "vi-VN",
    path: "/vi/notary",
    langName: "Tiếng Việt",
    title: "Công chứng tại Bangkok — Luật sư công chứng đăng ký | NYC Legal",
    description: "Dịch vụ công chứng tại Bangkok: chứng thực chữ ký, bản sao y, tuyên thệ, giấy ủy quyền, nghị quyết công ty. Từ 500 THB, trong ngày, dùng được ở hơn 120 quốc gia.",
    h1: "Dịch vụ Công chứng (Notary Public) tại Bangkok",
    hero: "Dịch vụ công chứng bởi Luật sư công chứng đăng ký với Đoàn Luật sư Thái Lan — hơn 30 loại giấy tờ dùng cho Apostille, hợp pháp hóa lãnh sự, kết hôn ở nước ngoài và ủy quyền bất động sản.",
    ctaConsult: "Tư vấn qua LINE / Zalo",
    ctaCall: "Gọi 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.vi,
    facts: facts("vi"),
    servicesH2: "6 nhóm dịch vụ công chứng",
    categories: {
      signature: "Chứng thực chữ ký (Acknowledgement / Witness)",
      affidavit: "Tuyên thệ / Jurat / Oath",
      copy: "Bản sao y có chứng thực (Certified True Copy)",
      corporate: "Nghị quyết công ty / Giấy ủy quyền (POA)",
      property: "Giấy tờ bất động sản / thừa kế",
      intl: "Thị thực / giấy tờ sử dụng ở nước ngoài",
    },
    stepsH2: "Quy trình 4 bước",
    steps: [
      { title: "1. Gửi tài liệu qua LINE / email", body: "Chụp/scan tài liệu và hộ chiếu, gửi qua LINE @nycli hoặc email. Chúng tôi xác nhận loại dịch vụ và chi phí trong 15 phút." },
      { title: "2. Đặt lịch hoặc đến văn phòng", body: "Văn phòng tại Bangkok (gần MRT Asoke). Thứ Hai đến thứ Bảy, 09:00–18:00." },
      { title: "3. Ký trước mặt luật sư công chứng", body: "Luật sư xác minh danh tính và chứng kiến chữ ký. Toàn bộ quy trình khoảng 20 phút." },
      { title: "4. Nhận tài liệu đã đóng dấu", body: "Nhận tài liệu đã công chứng ngay trong ngày. Chúng tôi có thể tiếp tục làm chứng nhận MFA và lãnh sự." },
    ],
    faqH2: "Câu hỏi thường gặp",
    faqs: [
      { q: "Việt Nam có chấp nhận Apostille từ Thái Lan không?", a: "Không — Việt Nam chưa tham gia Công ước La Hay. Cần đủ chuỗi: công chứng Thái → MFA Thái → Đại sứ quán Việt Nam tại Bangkok." },
      { q: "Tôi có cần dịch sang tiếng Việt không?", a: "Có — cơ quan Việt Nam yêu cầu bản dịch tiếng Việt do công ty dịch thuật được công nhận thực hiện." },
      { q: "Có nhân viên nói tiếng Việt không?", a: "Dịch vụ hỗ trợ bằng tiếng Việt sẵn sàng phục vụ quý khách. Chúng tôi cung cấp sự hỗ trợ bằng tiếng Việt để quá trình thực hiện thủ tục diễn ra thuận lợi." },
      { q: "Chi phí là bao nhiêu?", a: "Việc xử lý giấy tờ phụ thuộc vào từng loại giấy tờ cụ thể. Mỗi loại tài liệu sẽ được thực hiện theo quy trình tương ứng." },
      { q: "Có thể lấy trong ngày không?", a: "Việc hoàn thành trong ngày được áp dụng cho phần lớn các yêu cầu. Phần lớn công việc được xử lý và hoàn tất ngay trong ngày." },
    ],
    bottomNudge: "Cần gói dịch thuật + công chứng + hợp pháp hóa? Liên hệ qua LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "Điện thoại 083-249-4999",
  },
  {
    locale: "id",
    hreflang: "id-ID",
    path: "/id/notary",
    langName: "Bahasa Indonesia",
    title: "Notaris di Bangkok — Notaris-pengacara terdaftar | NYC Legal",
    description: "Layanan notaris di Bangkok: pengesahan tanda tangan, salinan resmi, pernyataan, surat kuasa, resolusi perusahaan. Mulai 500 THB, hari yang sama, berlaku di 120+ negara.",
    h1: "Layanan Notary Public di Bangkok",
    hero: "Layanan notaris oleh notaris-pengacara terdaftar di Dewan Pengacara Thailand — lebih dari 30 jenis akta untuk Apostille, legalisasi konsuler, pernikahan di luar negeri, dan surat kuasa properti.",
    ctaConsult: "Konsultasi via LINE / WhatsApp",
    ctaCall: "Telepon 083-249-4999",
    factsLabel: COMMON_FACTS_LABELS.id,
    facts: facts("id"),
    servicesH2: "6 kategori layanan notaris",
    categories: {
      signature: "Pengesahan tanda tangan (Acknowledgement / Witness)",
      affidavit: "Pernyataan / Jurat / Oath",
      copy: "Salinan resmi (Certified True Copy)",
      corporate: "Resolusi perusahaan / Surat kuasa (POA)",
      property: "Dokumen properti / warisan",
      intl: "Visa / dokumen untuk digunakan di luar negeri",
    },
    stepsH2: "4 langkah proses",
    steps: [
      { title: "1. Kirim dokumen via LINE / email", body: "Pindai dokumen dan paspor Anda, kirim melalui LINE @nycli atau email. Kami konfirmasi layanan dan biaya dalam 15 menit." },
      { title: "2. Buat janji atau kunjungi kantor", body: "Kantor di Bangkok (dekat MRT Asoke). Senin–Sabtu, 09:00–18:00." },
      { title: "3. Tanda tangani di hadapan notaris", body: "Notaris memverifikasi identitas Anda dan menyaksikan tanda tangan. Sekitar 20 menit." },
      { title: "4. Ambil dokumen bercap", body: "Ambil dokumen yang telah dinotarisasi pada hari yang sama. Bisa dilanjutkan legalisasi MFA + konsulat." },
    ],
    faqH2: "Pertanyaan yang sering diajukan",
    faqs: [
      { q: "Apakah Indonesia menerima Apostille dari Thailand?", a: "Indonesia sudah menjadi anggota Konvensi Apostille, tetapi keanggotaan Thailand baru berlaku pada 28 Februari 2027. Sampai tanggal tersebut dokumen Thailand harus melalui jalur konsuler: notaris-pengacara, lalu Departemen Konsuler Kementerian Luar Negeri Thailand, kemudian legalisasi KBRI di Bangkok." },
      { q: "Apakah perlu terjemahan Bahasa Indonesia?", a: "Terjemahan tersumpah ke Bahasa Indonesia disyaratkan oleh berbagai instansi di Indonesia. Instansi Indonesia mewajibkan dokumen resmi menggunakan terjemahan tersumpah ke Bahasa Indonesia." },
      { q: "Apakah ada staf berbahasa Indonesia?", a: "Pelayanan dalam Bahasa Indonesia tersedia untuk membantu pengurusan dokumen Anda. Kami melayani komunikasi dan konsultasi dalam Bahasa Indonesia secara langsung." },
      { q: "Berapa biayanya?", a: "Biaya pengurusan notaris dan legalisasi dihitung per jenis dokumen serta jumlah halaman yang diserahkan, karena setiap kategori dokumen memiliki kriteria penanganan dan langkah verifikasi yang berbeda. Silakan kirimkan pindaian dokumen Anda melalui LINE agar tim kami dapat memberikan rincian penawaran resmi sebelum pengerjaan dimulai." },
      { q: "Bisa selesai hari yang sama?", a: "Penyelesaian pada hari yang sama dapat dilakukan untuk sebagian besar pengurusan. Sebagian besar akta selesai diproses pada hari yang sama." },
    ],
    bottomNudge: "Butuh paket terjemahan + notaris + apostille? Hubungi kami via LINE.",
    contactLine: "LINE @nycli",
    contactPhone: "Telepon 083-249-4999",
  },
];

// All hreflang alternates for the /notary cluster — used in every locale page
export const NOTARY_HREFLANG_ALTS = [
  { hreflang: "th-TH", path: "/notary" },
  { hreflang: "en", path: "/en/notary" },
  ...NOTARY_LOCALES.map((l) => ({ hreflang: l.hreflang, path: l.path })),
];

export function getNotaryLocale(locale: string) {
  return NOTARY_LOCALES.find((l) => l.locale === locale);
}
